import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  approveBranchSaleReport,
  approveHoSaleReport,
  exportSaleReportWebIt,
  rejectSaleReport,
  requestChangeBranchSaleReport,
  requestChangeHoSaleReport,
  submitFinanceSaleReport,
  updateReconcileStatus,
} from "../../../services";
import { SALE_REPORT_STATUS } from "./reconcile";
import { STATUS_COLOR, STATUS_LABEL } from "../ReportList/data";
import { TabValue } from "../../../pages/ReportDetailsPage";
import {
  SaleReportHistory,
  SalesReportSummary,
} from "../../../types/sale-reports.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { formatThaiFullDate } from "../../../utils/date";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ConfirmModal from "../../ConfirmModal";
import RequestChangeBranchModal from "./RequestChangeBranchModal";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

type PendingAction =
  | "approve"
  | "request-change-branch"
  | "request-change-ho"
  | "reject";

type ConfirmablePendingAction = Exclude<
  PendingAction,
  "request-change-branch"
>;

const CONFIRM_MODAL_TEXT: Record<
  ConfirmablePendingAction,
  { title: string; description: string }
> = {
  approve: {
    title: "ยืนยันอัพเดทข้อมูลรายงาน",
    description: "คุณแน่ใจหรือไม่ว่าต้องการอัพเดทข้อมูลรายงานนี้?",
  },
  // request-change-branch is NOT here: it collects a required reason, so it
  // uses RequestChangeBranchModal instead of the yes/no ConfirmModal.
  "request-change-ho": {
    title: "ยืนยันขอแก้ไขรายงาน",
    description: "คุณแน่ใจหรือไม่ว่าต้องการส่งคำขอแก้ไขรายงานนี้?",
  },
  reject: {
    title: "ยืนยันปฏิเสธรายงาน",
    description: "คุณแน่ใจหรือไม่ว่าต้องการปฏิเสธรายงานนี้?",
  },
};

interface DetailsFooterProps {
  tab: TabValue;
  reportSummary?: SalesReportSummary;
  reportId?: string;
  reportHistory?: SaleReportHistory[];
  selectedHistory?: SaleReportHistory;
  onChangeVersion?: (history: SaleReportHistory) => void;
  setTriggerFetch?: Dispatch<SetStateAction<number>>;
}

export default function DetailsFooter({
  tab,
  reportSummary,
  reportId,
  reportHistory,
  selectedHistory,
  onChangeVersion,
  setTriggerFetch,
}: DetailsFooterProps) {
  const {
    isBranch,
    isHeadOfficeAccounting,
    isBranchManager,
    isBranchFinance,
    isBranchAccounting,
    isBranchFinanceAndAccount,
  } = useUserRole();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  // Every action behind this dialog re-versions the sales report, and the API
  // takes long enough to approve one that a second click lands before the first
  // response. Two approvals of the same report used to write two open versions,
  // which duplicated the row in the report list for good.
  const [submitting, setSubmitting] = useState(false);

  const reportStatus = useMemo(() => {
    return reportSummary?.status;
  }, [reportSummary?.status]);

  // Who may APPROVE a branch report — the same two roles the API lets call
  // approve-branch / request-change-branch. A finance-only user can submit and
  // re-submit, never approve.
  const isAccountingDesk = Boolean(
    isBranchAccounting || isBranchFinanceAndAccount,
  );

  // A report accounting sent back is worked by BOTH desks, so the primary
  // button means different things to each: finance re-submits it (back to
  // รอบัญชีตรวจสอบ), accounting — who may have corrected it themselves —
  // approves it outright.
  const isSentBackToFinance =
    reportStatus === SALE_REPORT_STATUS.BRANCH_ACCOUNT_REQUEST_CHANGE;
  const isFinanceResubmit = isSentBackToFinance && !isAccountingDesk;

  const handleApprove = useCallback(
    async (uuid: string) => {
      const isDraft = reportStatus === "draft";
      const isHoApprove =
        isHeadOfficeAccounting && reportStatus === "branch account approved";
      // A finance re-submit lands the reconcile in the same state a first
      // submit does — it is the same step of the workflow, run twice.
      const isSubmitPath = isDraft || isFinanceResubmit;
      const reconcileStatus = isSubmitPath
        ? "finance approved"
        : "account approved";

      if (isSubmitPath) {
        await submitFinanceSaleReport(uuid);
      } else if (isHoApprove) {
        await approveHoSaleReport(uuid);
      } else {
        await approveBranchSaleReport(uuid);
      }

      if (reportSummary?.waste_checker_id) {
        await updateReconcileStatus(
          reportSummary.waste_checker_id,
          reconcileStatus,
        );
      }
    },
    [
      reportStatus,
      isHeadOfficeAccounting,
      isFinanceResubmit,
      reportSummary?.waste_checker_id,
    ],
  );

  const handleDownloadReport = async () => {
    if (!reportId) return;

    const response = await exportSaleReportWebIt(reportId);
    saveBlobResponse(response, `sales-report-${reportId}.xlsx`);
  };

  const onUpdateSaleReport = async () => {
    if (!reportId || !pendingAction || submitting) return;

    setSubmitting(true);
    try {
      if (pendingAction === "approve") {
        await handleApprove(reportId);
      } else if (pendingAction === "request-change-ho") {
        await requestChangeHoSaleReport(reportId);
      } else if (pendingAction === "reject") {
        await rejectSaleReport(reportId);
      }

      setTriggerFetch?.((prev) => prev + 1);
      setPendingAction(null);
    } finally {
      setSubmitting(false);
    }
  };

  const buttonText = useMemo(() => {
    switch (reportStatus) {
      case "draft":
        return "ส่งข้อมูล";
      case SALE_REPORT_STATUS.BRANCH_ACCOUNT_REQUEST_CHANGE:
        return isFinanceResubmit ? "ส่งข้อมูลอีกครั้ง" : "อนุมัติ";
      case "branch finance submitted":
      case "ho request change":
      case "branch account approved":
        return "อนุมัติ";
      default:
        return "";
    }
  }, [reportStatus, isFinanceResubmit]);

  // The send-back is offered exactly where the gap was: a report sitting at
  // รอบัญชีตรวจสอบ, viewed by the desk that would otherwise only be able to
  // approve it. Labelled แก้ไข — the dialog behind it is where "this goes back
  // to finance" is spelled out.
  const canSendBackToFinance =
    isAccountingDesk && reportStatus === "branch finance submitted";

  const onRequestChangeBranch = async (reviewNote: string) => {
    if (!reportId) return;
    await requestChangeBranchSaleReport(reportId, reviewNote);
    setTriggerFetch?.((prev) => prev + 1);
    setPendingAction(null);
  };

  const isDisableBranchSubmit =
    reportStatus === "branch finance submitted" &&
    (!isBranch || isBranchFinance);

  const isHoActionState =
    isHeadOfficeAccounting && reportStatus === "branch account approved";

  // Outside its own review state, HO has no action on a branch report: every
  // remaining button posts to a branch-scoped endpoint that would 403.
  const hideActionsForHo = Boolean(isHeadOfficeAccounting) && !isHoActionState;

  // request-change-branch collects a note, so it gets its own dialog.
  const confirmAction: ConfirmablePendingAction | null =
    pendingAction && pendingAction !== "request-change-branch"
      ? pendingAction
      : null;

  const isDisabled =
    isDisableBranchSubmit ||
    !reportSummary?.sales_target ||
    reportStatus === "branch account approved";

  if (!selectedHistory || !reportHistory) return null;

  return (
    <>
      <Paper
        sx={{
          // Anchor to the main content area, not the viewport: offset the left
          // edge by the live sidebar width (published as a CSS var by Sidebar)
          // so the bar no longer slides under the sidebar / overflows the right.
          left: "var(--gb-sidebar-width, 0px)",
          right: 0,
          height: "66px",
          position: "fixed",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "24px",
          boxShadow: "0 -4px 24px -8px rgba(0,0,0,0.10)",
          [compact]: {
            height: "auto",
            minHeight: "52px",
            padding: "8px 16px",
            gap: 1,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ minWidth: 0, mr: 2, [compact]: { gap: 0.5, mr: 1 } }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              // @ts-ignore
              bgcolor: STATUS_COLOR?.[reportStatus],
            }}
          />
          <Typography
            variant="body2"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {/* @ts-ignore */}
            {STATUS_LABEL[reportStatus]}
          </Typography>
          <Typography
            fontWeight={600}
            variant="caption"
            color="text.secondary"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            แก้ไขโดยครั้งที่ {selectedHistory.created_no} โดย
            <Typography variant="caption" component="span" fontWeight={800}>
              {" "}
              {selectedHistory.created_by} ({selectedHistory.created_role}){" "}
            </Typography>
            เมื่อ
          </Typography>
          <Select
            size="small"
            sx={{ flexShrink: 0 }}
            value={selectedHistory?.created_no ?? ""}
            onChange={(e) => {
              const found = reportHistory.find(
                (history) => history.created_no === e.target.value,
              );
              if (found) onChangeVersion?.(found);
            }}
          >
            {reportHistory.map((history) => (
              <MenuItem key={history.created_no} value={history.created_no}>
                <Typography variant="caption" component="span" fontWeight={800}>
                  {formatThaiFullDate(history.created_at)}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box display="flex" gap={1} sx={{ flexShrink: 0 }}>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={handleDownloadReport}
          >
            ดาวน์โหลดรายงาน
          </Button>
          {tab === "report" &&
            !isBranchManager &&
            !hideActionsForHo &&
            reportStatus !== "rejected" &&
            reportStatus !== "ho approved" &&
            (isHoActionState ? (
              <>
                <Button
                  size="large"
                  variant="outlined"
                  color="warning"
                  onClick={() => setPendingAction("request-change-ho")}
                >
                  ขอแก้ไข
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={!reportSummary?.sales_target}
                  onClick={() => setPendingAction("approve")}
                >
                  ยื่นรอส่ง E-tax
                </Button>
              </>
            ) : (
              <>
                {canSendBackToFinance && (
                  <Button
                    size="large"
                    variant="outlined"
                    color="warning"
                    onClick={() => setPendingAction("request-change-branch")}
                  >
                    แก้ไข
                  </Button>
                )}
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={isDisabled}
                  onClick={() => setPendingAction("approve")}
                >
                  {buttonText}
                </Button>
              </>
            ))}
        </Box>
      </Paper>
      {confirmAction && (
        <ConfirmModal
          title={CONFIRM_MODAL_TEXT[confirmAction].title}
          description={CONFIRM_MODAL_TEXT[confirmAction].description}
          open
          loading={submitting}
          onClose={() => {
            if (!submitting) setPendingAction(null);
          }}
          onConfirm={onUpdateSaleReport}
        />
      )}
      <RequestChangeBranchModal
        open={pendingAction === "request-change-branch"}
        onClose={() => setPendingAction(null)}
        onConfirm={onRequestChangeBranch}
      />
    </>
  );
}
